import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import styles from './CommunityNoteIcon.module.css';
import { logger } from '../utils/logger';

interface CommunityNote {
  id: string;
  class_name: string;
  method_name?: string;
  property_name?: string;
  content: string;
  created_at: string;
  updated_at: string;
  author_username: string;
  author_avatar: string;
}

interface CommunityNoteIconProps {
  className: string;
  methodName?: string;
  propertyName?: string;
}

export const CommunityNoteIcon: React.FC<CommunityNoteIconProps> = ({
  className,
  methodName,
  propertyName
}) => {
  const [notes, setNotes] = useState<CommunityNote[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLButtonElement>(null);
  const { isAuthenticated, getAuthToken } = useAuth();

  // Fetch notes when component mounts or when icon is hovered
  useEffect(() => {
    if (showPopup && notes.length === 0 && !loading) {
      fetchNotes();
    }
  }, [showPopup]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        class_name: className
      });
      if (methodName) params.append('method_name', methodName);
      if (propertyName) params.append('property_name', propertyName);

      const response = await fetch(`/api/notes/list?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setNotes(data.notes);
      }
    } catch (error) {
      logger.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNote = async () => {
    if (!noteContent.trim()) return;

    setSubmitting(true);
    try {
      const token = getAuthToken();
      const response = await fetch('/api/notes/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          class_name: className,
          method_name: methodName,
          property_name: propertyName,
          content: noteContent
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Note submitted for moderation!');
        setNoteContent('');
        setShowAddNote(false);
        // Refresh notes list
        await fetchNotes();
      } else {
        logger.error('Note submission error:', data);
        const errorMsg = data.details || data.error || 'Unknown error';
        const hint = data.hint ? `\n\n${data.hint}` : '';
        alert(`Error submitting note: ${errorMsg}${hint}`);
      }
    } catch (error) {
      logger.error('Error submitting note:', error);
      alert('Error submitting note: ' + (error instanceof Error ? error.message : 'Network error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Close popup when clicking outside or when mouse leaves both icon and popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node) &&
          iconRef.current && !iconRef.current.contains(event.target as Node)) {
        setShowPopup(false);
        setShowAddNote(false);
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPopup]);

  // Close popup when hovering away from both icon and popup
  const handleMouseLeave = () => {
    // Small delay to allow moving from icon to popup
    setTimeout(() => {
      if (iconRef.current && popupRef.current) {
        const isHoveringIcon = iconRef.current.matches(':hover');
        const isHoveringPopup = popupRef.current.matches(':hover');
        if (!isHoveringIcon && !isHoveringPopup) {
          setShowPopup(false);
          setShowAddNote(false);
        }
      }
    }, 100);
  };

  const hasNotes = notes.length > 0;

  return (
    <span className={styles.noteIconContainer}>
      <button
        ref={iconRef}
        className={`${styles.noteIcon} ${hasNotes ? styles.hasNotes : ''}`}
        onMouseEnter={() => setShowPopup(true)}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          e.stopPropagation();
          setShowPopup(!showPopup);
        }}
        aria-label="Community Notes"
        title={hasNotes ? `${notes.length} community note${notes.length > 1 ? 's' : ''}` : 'No community notes yet'}
      >
        📝
        {hasNotes && <span className={styles.noteCount}>{notes.length}</span>}
      </button>

      {showPopup && (
        <div
          ref={popupRef}
          className={styles.notePopup}
          onMouseLeave={handleMouseLeave}
        >
          <div className={styles.noteHeader}>
            <h4>Community Notes</h4>
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowPopup(false);
                setShowAddNote(false);
              }}
            >
              ×
            </button>
          </div>

          {loading && (
            <div className={styles.loading}>Loading notes...</div>
          )}

          {!loading && notes.length === 0 && !showAddNote && (
            <div className={styles.noNotes}>
              <p>No community notes yet.</p>
              {isAuthenticated && (
                <button
                  className={styles.addNoteButton}
                  onClick={() => setShowAddNote(true)}
                >
                  Add First Note
                </button>
              )}
            </div>
          )}

          {!loading && notes.length > 0 && (
            <div className={styles.notesList}>
              {notes.map(note => (
                <div key={note.id} className={styles.noteItem}>
                  <div className={styles.noteAuthor}>
                    <img
                      src={note.author_avatar}
                      alt={note.author_username}
                      className={styles.authorAvatar}
                    />
                    <div className={styles.authorInfo}>
                      <span className={styles.authorName}>{note.author_username}</span>
                      <span className={styles.noteDate}>
                        {new Date(note.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className={styles.noteContent}>
                    {note.content}
                  </div>
                </div>
              ))}

              {isAuthenticated && !showAddNote && (
                <button
                  className={styles.addNoteButton}
                  onClick={() => setShowAddNote(true)}
                >
                  Add Note
                </button>
              )}
            </div>
          )}

          {showAddNote && (
            <div className={styles.addNoteForm}>
              <textarea
                className={styles.noteTextarea}
                placeholder="Add your note here... (max 2000 characters)"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                maxLength={2000}
                rows={5}
              />
              <div className={styles.formActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setShowAddNote(false);
                    setNoteContent('');
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className={styles.submitButton}
                  onClick={handleSubmitNote}
                  disabled={!noteContent.trim() || submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Note'}
                </button>
              </div>
              <p className={styles.moderationNote}>
                Your note will be reviewed by moderators before appearing publicly.
              </p>
            </div>
          )}

          {!isAuthenticated && !loading && (
            <div className={styles.loginPrompt}>
              <p>Login to add community notes</p>
            </div>
          )}
        </div>
      )}
    </span>
  );
};
